import { EXTERNAL_LINKS, useSecureGraphqlMutation, useSecureGraphqlQuery } from '@psyomics/ui-common';
import { Button, PageLayout, Puck } from '@psyomics/ui-components';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  GetSelfReferralServiceDocument,
  GetSelfReferralServiceQuery,
  InitialiseSelfReferralDocument,
  InitialiseSelfReferralInput,
  InitialiseSelfReferralMutation,
} from './generated/self-referral';

const useStartSurvey = (selfReferralCode: string) => {
  const [{ fetching, data }, executeMutation] = useSecureGraphqlMutation<
    InitialiseSelfReferralMutation,
    { input: InitialiseSelfReferralInput }
  >(InitialiseSelfReferralDocument);

  const navigate = useNavigate();

  const startSurvey = useCallback(() => {
    executeMutation({ input: { selfReferralCode } });
  }, [selfReferralCode, executeMutation]);
  const publicId = data?.initialiseSelfReferral.publicId;

  useEffect(() => {
    if (!fetching && publicId) {
      navigate(`/questionnaire/${publicId}`);
    }
  }, [publicId, fetching, navigate]);

  return { startSurvey, fetching };
};

const SelfReferralInit = ({ selfReferralCode }: { readonly selfReferralCode: string }) => {
  const { t } = useTranslation();
  const [{ data: queryData }] = useSecureGraphqlQuery<GetSelfReferralServiceQuery>({
    query: GetSelfReferralServiceDocument,
    variables: {
      input: { selfReferralCode },
    },
  });
  const { startSurvey, fetching } = useStartSurvey(selfReferralCode);
  if (!queryData) return null;
  const content = queryData.getSelfReferralService.landingPageContent;

  return (
    <PageLayout>
      {content && (
        <div className="mb-8">
          <Puck.ContentPage.Render
            metadata={{
              orgName: queryData.getSelfReferralService.organisation.name,
              orgNameAbbreviation: queryData.getSelfReferralService.organisation.shortName ?? undefined,
              orgPrivacyPolicyUrl: queryData.getSelfReferralService.organisation.privacyPolicyUrl ?? undefined,
              psyomicsPrivacyPolicyUrl: EXTERNAL_LINKS.PRIVACY_POLICY,
            }}
            reportDefinition={JSON.parse(content)}
          />
        </div>
      )}
      <div>
        <Button disabled={fetching} onClick={startSurvey}>
          {t('auth.register.startSurveyButtonText')}
        </Button>
      </div>
    </PageLayout>
  );
};

export { SelfReferralInit };
